import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';

import { getCompetence } from '@/store/competence/actions';
import { getAllTags, setTag } from '@/store/tags/actions';
import { getTopicCategories } from '@/store/topics/actions';
import { getSkills } from '@/store/user_skills/actions';

import { selectCompetence } from '@/store/competence/selectors';
import { selectTags } from '@/store/tags/selectors';
import { selectTopics } from '@/store/topics/selectors';
import { selectTracks } from '@/store/tracks/selectors';
import { skills as skillsSelector } from '@/store/user_skills/selectors';
import { selectUsers } from '@/store/users/selectors';

import { Button, Col, Form, Input, Row, Select } from 'antd';

import useIsMobile from '@shared/hooks/useIsMobile';

import css from './CollectionTracksFilter.module.scss';

const CollectionTracksFilter = ({ form, onSubmit, onReset }) => {
  const { t } = useTranslation('CollectionTracksFilter');
  const dispatch = useDispatch();
  const [isMobile] = useIsMobile(768);
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();

  const { currentUser } = useSelector(selectUsers);
  const { competence, isLoading: isCompetenceLoading } = useSelector(selectCompetence);
  const { skills, isLoading: isSkillsLoading } = useSelector(skillsSelector);
  const { allTags, isLoading: isTagsLoading } = useSelector(selectTags);
  const { categories, isLoading: isCategoriesLoading } = useSelector(selectTopics);
  const { collectionTracksSort } = useSelector(selectTracks);

  const handleSubmit = () => {
    const values = form.getFieldsValue();
    const newSearchParams = new URLSearchParams();

    // Добавляем остальные параметры
    Object.entries(values).forEach(([key, value]) => {
      if (value) {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach(item => {
            newSearchParams.append(key, item);
          });
        } else {
          newSearchParams.set(key, value);
        }
      }
    });

    // Сохраняем сортировку
    newSearchParams.set('sort', collectionTracksSort);

    setSearchParams(newSearchParams);
    onSubmit();
  };

  // Заполнение формы из searchParams
  useEffect(() => {
    if (!form) return; // Добавляем проверку на существование формы

    const getParam = paramName => {
      const values = searchParams.getAll(paramName).map(Number);
      return values.length > 0 ? values : undefined;
    };

    const formValues = {
      name: searchParams.get('name') || undefined,
      skillIds: getParam('skillIds'),
      tagIds: getParam('tagIds'),
      competenceIds: getParam('competenceIds'),
      categoryIds: getParam('categoryIds'),
    };

    // Это условие нужно для того, чтобы при переходе на страницу
    // collection-tracks не отображалось id option'а без label
    if (state?.tagName && searchParams.get('tagIds')) {
      dispatch(setTag({ name: state.tagName, id: Number(searchParams.get('tagIds')) }));
    }

    form.setFieldsValue(formValues);
  }, [searchParams, form, state?.tagName]);

  // Получение option'ов для фильтра
  useEffect(() => {
    const isGlobalOrSharing = currentUser?.domainCompany?.global ? { isGlobal: true } : { isSharing: false };

    dispatch(getCompetence('', 0, () => {}, false, true));

    // Получение навыков /api/v1/skill
    dispatch(
      getSkills({
        withoutTopics: true,
        ...isGlobalOrSharing,
        size: 1000,
      })
    );

    // Получение тегов /api/v1/tag
    dispatch(
      getAllTags({
        ...isGlobalOrSharing,
        size: 1000,
      })
    );

    dispatch(getTopicCategories(currentUser?.domainCompany?.global));
  }, []);

  return (
    <div className={css['CollectionTracksFilter']} data-qa='filterBlock'>
      <h2 className={css['CollectionTracksFilter-title']}>{t('title')}</h2>
      <Form form={form} layout='vertical'>
        <Form.Item name='name'>
          <Input placeholder={t('trackName')} allowClear />
        </Form.Item>
        <Row gutter={16}>
          <Col span={isMobile ? 24 : 12}>
            <Form.Item name='skillIds' label={t('skills')}>
              <Select
                placeholder={t('skillsPlaceholder')}
                allowClear={!isMobile}
                mode='multiple'
                options={skills}
                optionFilterProp='name'
                fieldNames={{ label: 'name', value: 'id' }}
                loading={isSkillsLoading}
              />
            </Form.Item>
          </Col>
          <Col span={isMobile ? 24 : 12}>
            <Form.Item name='tagIds' label={t('tags')}>
              <Select
                placeholder={t('tagsPlaceholder')}
                allowClear={!isMobile}
                mode='multiple'
                options={allTags}
                optionFilterProp='name'
                fieldNames={{ label: 'name', value: 'id' }}
                loading={isTagsLoading}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={isMobile ? 24 : 12}>
            <Form.Item name='competenceIds' label={t('competencies')}>
              <Select
                placeholder={t('competenciesPlaceholder')}
                allowClear={!isMobile}
                mode='multiple'
                options={competence?.content}
                optionFilterProp='name'
                fieldNames={{ label: 'name', value: 'id' }}
                loading={isCompetenceLoading}
              />
            </Form.Item>
          </Col>
          <Col span={isMobile ? 24 : 12}>
            <Form.Item name='categoryIds' label={t('category')}>
              <Select
                placeholder={t('categoryPlaceholder')}
                allowClear={!isMobile}
                mode='multiple'
                options={categories}
                optionFilterProp='name'
                fieldNames={{ label: 'name', value: 'id' }}
                loading={isCategoriesLoading}
              />
            </Form.Item>
          </Col>
        </Row>
        <div className={css['CollectionTracksFilter-footer']}>
          <Button type='default' onClick={onReset}>
            {t('reset')}
          </Button>
          <Button type='primary' onClick={handleSubmit}>
            {t('apply')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CollectionTracksFilter;
